import * as React from "react"
import { router, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { trackConversion, visitSucceeded } from "@/lib/analytics"
import { FormError, useHoneypot } from "@/components/public-form"
import type { PageProps } from "@/types/inertia"

function FieldError({ message }: { message?: string }) {
  if (!message) return null
  return <p className="mt-1 text-xs text-danger-display">{message}</p>
}

// The venue's own enquiry form — the counterpart to Gisela's EnquiryForm, in
// the same shape but its own questions and its own inbox. It posts to
// /contact, which redirect_backs to this page; the fragment is restored by
// hand because Inertia's fetch-based redirects drop it.
//
// The enquiry type is the fork the contact page's two routing cards used to
// be: a room, a therapy session, or something else.
export function ContactForm() {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}
  const t = props.translations.contact

  const [name, setName] = React.useState("")
  const [email, setEmail] = React.useState("")
  const [phone, setPhone] = React.useState("")
  const [enquiryType, setEnquiryType] = React.useState("")
  const [message, setMessage] = React.useState("")
  const [processing, setProcessing] = React.useState(false)
  const honeypot = useHoneypot("sc-company")

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setProcessing(true)
    router.post(
      "/contact",
      {
        contact_submission: { name, email, phone, enquiry_type: enquiryType, message },
        ...honeypot.payload,
      },
      {
        onSuccess: (page) => {
          if (visitSucceeded(page)) trackConversion("enquiry")
        },
        onFinish: () => {
          setProcessing(false)
          window.location.hash = "enquiry-form"
        },
      },
    )
  }

  return (
    <form onSubmit={handleSubmit} className="mt-8 space-y-5">
      <FormError message={errors.base} />
      {honeypot.field}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="sc-name">{t.name}</label>
          <Input
            id="sc-name"
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-invalid={!!errors.name}
            className="mt-1"
          />
          <FieldError message={errors.name} />
        </div>
        <div>
          <label htmlFor="sc-email">{t.email}</label>
          <Input
            id="sc-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={!!errors.email}
            className="mt-1"
          />
          <FieldError message={errors.email} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="sc-phone">{t.phone}</label>
          <Input
            id="sc-phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            aria-invalid={!!errors.phone}
            className="mt-1"
          />
          <FieldError message={errors.phone} />
        </div>
        <div>
          <label htmlFor="sc-enquiry-type">{t.enquiry_type}</label>
          <Select
            id="sc-enquiry-type"
            required
            value={enquiryType}
            onChange={(e) => setEnquiryType(e.target.value)}
            aria-invalid={!!errors.enquiry_type}
            className="mt-1"
          >
            <option value="">{t.select_placeholder}</option>
            <option value="room_hire">{t.types.room_hire}</option>
            <option value="therapy_session">{t.types.therapy_session}</option>
            <option value="other">{t.types.other}</option>
          </Select>
          <FieldError message={errors.enquiry_type} />
        </div>
      </div>

      <div>
        <label htmlFor="sc-message">{t.message}</label>
        {/* form-control alone is a one-line h-10 input — the design system's
            textarea sizing is the second class. */}
        <textarea
          id="sc-message"
          rows={7}
          required
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          aria-invalid={!!errors.message}
          className="form-control form-control-textarea mt-1 w-full resize-y"
        />
        <FieldError message={errors.message} />
      </div>

      <div className="pt-2">
        <Button type="submit" variant="primary" size="lg" disabled={processing} className="w-full sm:w-auto">
          {t.submit}
        </Button>
      </div>
    </form>
  )
}

// The whole "Get in touch" band: heading, intro or thank-you, and the form.
export function ContactFormSection() {
  const { props } = usePage<PageProps>()
  const notice = props.flash.notice
  const t = props.translations.contact

  return (
    <section id="enquiry-form" className="bg-cream py-16">
      <div className="mx-auto max-w-2xl px-6 lg:px-8">
        <h2>{t.title}</h2>

        {notice ? (
          <div className="mt-8 rounded-2xl bg-white p-8 text-center ring-1 ring-hairline">
            <h3>{t.success_heading}</h3>
            <p className="mt-3 text-ink-muted">{notice}</p>
          </div>
        ) : (
          <>
            <p className="mt-3 text-ink-muted">{t.intro}</p>
            <ContactForm />
          </>
        )}
      </div>
    </section>
  )
}
